import baseApi from "./RootBaseApi";
import {
  PaginatedPostResponse,
  ReportedPostResponse,
  Post,
} from "@/types/post-management.types";

export enum PostAction {
  FLAG = "FLAG",
  HIDE = "HIDE",
  DELETE = "DELETE",
  APPROVE = "APPROVE",
  REJECT = "REJECT",
}

export const PostService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllPosts: builder.query<
      PaginatedPostResponse,
      {
        page?: number;
        limit?: number;
        status?: string;
        search?: string;
        community_id?: string;
      }
    >({
      query: ({ community_id, ...params }) => ({
        url: `communities/${community_id}/posts`,
        method: "GET",
        params: {
          page: params.page,
          limit: params.limit,
          status: params.status,
          search: params.search,
        },
      }),
      transformResponse: (response: PaginatedPostResponse) => response,
      providesTags: ["Posts"],
    }),
    getPostById: builder.query<Post, { communityId: string; postId: string }>({
      query: ({ communityId, postId }) => ({
        url: `communities/${communityId}/posts/${postId}`,
        method: "GET",
      }),
      transformResponse: (response: Post) => response,
      providesTags: (result, error, { postId }) => [
        { type: "Posts", id: postId },
      ],
    }),
    getReportedPosts: builder.query<
      ReportedPostResponse,
      {
        page?: number;
        limit?: number;
        search?: string;
        community_id?: string;
      }
    >({
      query: ({ community_id, ...params }) => ({
        url: `communities/posts/reports`,
        method: "GET",
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          community_id,
        },
      }),
      transformResponse: (response: ReportedPostResponse) => response,
      providesTags: ["ReportedPosts"],
    }),
    moderatePost: builder.mutation<
      void,
      {
        postId: string;
        action: PostAction;
        reason?: string;
        reasonDetails?: string | null;
      }
    >({
      query: ({ postId, action, reason, reasonDetails }) => ({
        url: `communities/posts/${postId}/moderate`,
        method: "PATCH",
        data: {
          action,
          ...(reason && { reason }),
          ...(reasonDetails !== undefined && { reasonDetails }),
        },
      }),
      invalidatesTags: ["Posts", "ReportedPosts"],
    }),
    updatePost: builder.mutation<
      Post & { warning_message?: string },
      {
        communityId: string;
        postId: string;
        data: FormData | Record<string, unknown>;
      }
    >({
      query: ({ communityId, postId, data }) => ({
        url: `communities/${communityId}/posts/${postId}`,
        method: "PATCH",
        data,
      }),
      transformResponse: (response: Post & { warning_message?: string }) =>
        response,
      invalidatesTags: (result, error, { postId }) => [
        "Posts",
        { type: "Posts", id: postId },
      ],
    }),
    deletePost: builder.mutation<void, { communityId: string; postId: string }>(
      {
        query: ({ communityId, postId }) => ({
          url: `communities/${communityId}/posts/${postId}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Posts"],
      },
    ),
  }),
});

export const {
  useGetAllPostsQuery,
  useGetPostByIdQuery,
  useGetReportedPostsQuery,
  useModeratePostMutation,
  useUpdatePostMutation,
  useDeletePostMutation,
} = PostService;
